import { format } from 'date-fns'
import { MODULE_FIELD_TYPES } from "../constants"
import { SPECIAL_FIELD_RULES } from "../constants/specialFieldRules"

/**
 * Validates the special form fields based on the selected dropdown options.
 * If any required fields are missing, it sets the corresponding error messages using setError.
 * @param {string} module_code_key - The module code key.
 * @param {object} formData - The form data object.
 * @param {array} dropdownOptions - The dropdown options array.
 * @param {function} setError - The function to set form errors.
 * @returns {boolean} - Indicates whether there are any form errors.
 */
export const validateSpecialForm = (module_code_key, formData, dropdownOptions, setError) => {
  // Initialize formError flag as false
  let formError = false;

  // Validate special field in the template module
  if (module_code_key === 'template' && formData['is_channel_email'] === false) {
    formData['email_from'] = "";
  }

  // Check if the 'Exclusion Reason' dropdown option is set to 'Other' and the 'other_reason' field is empty
  if (formData['exclusion_reason_dropdown_option_id'] && dropdownOptions[formData['exclusion_reason_dropdown_option_id']] && dropdownOptions[formData['exclusion_reason_dropdown_option_id']].code_key === 'exclusion_reason_other' && formData['other_reason'] === "") {
    setError("other_reason", { type: "manual", message: "Field is required when the 'Exclusion Reason' is set to 'Other'" });
    formError = true;
  }

  // Check if the 'Exclusion Requested By' dropdown option is set to 'Other' and the 'other_requested_by' field is empty
  if (formData['exclusion_requested_by_dropdown_option_id'] && dropdownOptions[formData['exclusion_requested_by_dropdown_option_id']] && dropdownOptions[formData['exclusion_requested_by_dropdown_option_id']].code_key === 'exclusion_requested_by_other' && formData['other_requested_by'] === "") {
    setError("other_requested_by", { type: "manual", message: "Field is required when the 'Exclusion Requested By' is set to 'Other'" });
    formError = true;
  }

  // Check if the 'Exclusion Requested By' dropdown option is set to 'Other' and the 'other_requested_by' field is empty
  if (formData['discount_reason_dropdown_option_id'] && dropdownOptions[formData['discount_reason_dropdown_option_id']] && dropdownOptions[formData['discount_reason_dropdown_option_id']].code_key === 'discount_reason_waive_other' && formData['discount_reason_other'] === "") {
    setError("discount_reason_other", { type: "manual", message: "Field is required when the 'Discount Reason' is set to 'Other'" });
    formError = true;
  } else if (formData['discount_reason_dropdown_option_id'] && dropdownOptions[formData['discount_reason_dropdown_option_id']] && dropdownOptions[formData['discount_reason_dropdown_option_id']].code_key !== 'discount_reason_waive_other') {
    formData['discount_reason_other'] = "";
  }

  //Loan Agreement
  if (module_code_key === 'loan_agreement') {
    const isLateFeeFixed = dropdownOptions[formData['late_fee_calculation_dropdown_option_id']] && dropdownOptions[formData['late_fee_calculation_dropdown_option_id']].code_key == 'late_fee_calculation_fixed';
    if (!isLateFeeFixed && !formData['late_fee_principal_percentage']) {
      setError("late_fee_principal_percentage", { type: "manual", message: "Field is required when It Is Not Late Fee Fixed" });
      formError = true;
    }
    if (!isLateFeeFixed && !formData['late_fee_interest_percentage']) {
      setError("late_fee_interest_percentage", { type: "manual", message: "Field is required when It Is Not Late Fee Fixed" });
      formError = true;
    }
    if (isLateFeeFixed && !formData['late_fee']) {
      setError("late_fee", { type: "manual", message: "Field is required when It Is Late Fee Fixed" });
      formError = true;
    }
  }

  // Return the formError flag indicating whether there are any form errors
  return formError;
};

/**
 * Format form's data before sending to the server
 * @param {object} formData
 * @param module
 * @param dropdownOptions
 */
export function formDataSanitizer(formData, module, dropdownOptions) {
  Object.keys(formData).forEach((key) => {
    const moduleField = module.module_fields.find((e) => e.column_name == key);
    const fieldType = dropdownOptions[moduleField?.module_field_type_dropdown_option_id]?.code_key;

    if (formData[key]) {
      if (fieldType === MODULE_FIELD_TYPES.DATETIME.code_key) {
        formData[key] = format(new Date(formData[key]), 'yyyy-MM-dd HH:mm:ss')
      }
    }
  })
  return formData
}

/**
 * Validate string is URL
 * @param {string} str - Possible link
 */
const URL_PATTERN = process.env.REACT_APP_ENVIRONMENT !== 'production'
  ? new RegExp('^(https?:\\/\\/)?' // protocol
    + '(localhost|([a-z\\d]([a-z\\d-]*[a-z\\d])*\\.)+[a-z]{2,}|(\\d{1,3}\\.){3}\\d{1,3})' // localhost, domain, or IP
    + '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' // port and path
    + '(\\?[;&a-z\\d%_.~+=-]*)?' // query string
    + '(\\#[-a-z\\d_]*)?$', 'i')
  : new RegExp('^(https?:\\/\\/)?' // protocol
    + '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' // domain name
    + '((\\d{1,3}\\.){3}\\d{1,3}))' // OR ip (v4) address
    + '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' // port and path
    + '(\\?[;&a-z\\d%_.~+=-]*)?' // query string
    + '(\\#[-a-z\\d_]*)?$', 'i');

export const urlValidationPattern = { value: URL_PATTERN, message: 'Invalid URL' }

export const validURL = (str) => !!URL_PATTERN.test(str)

/**
 * Distinct CONTROL columns whose values drive the conditional reveal rules for a module. `createOnly`
 * rules (currently just tag/rc_tag_id) apply on the create form only, so they are excluded when
 * isCreate is false — preserving the edit form's behavior of not watching should_create_tag_in_rc.
 * @param {string} moduleCodeKey
 * @param {boolean} isCreate
 * @returns {string[]}
 */
const controlFieldsFor = (moduleCodeKey, isCreate) => {
  const rules = SPECIAL_FIELD_RULES[moduleCodeKey]
  if (!rules) return []
  return [...new Set(
    Object.values(rules)
      .filter((rule) => isCreate || !rule.createOnly)
      .map((rule) => rule.watch),
  )]
}

/**
 * Build the watched-values object a module form needs to evaluate its reveal rules. Each form keeps
 * ownership of its per-form DEFAULTS via `resolveDefault` (create: false for the email-channel toggle
 * and null otherwise; edit: row[column_name]) — passing them in is load-bearing, because a shared
 * hardcoded default would break initial visibility on edit (e.g. email_from would start hidden on an
 * existing email template).
 * @param {string} moduleCodeKey
 * @param {boolean} isCreate
 * @param {Function} watch            react-hook-form's `watch`
 * @param {Function} resolveDefault   (controlColumnName) => default value for this form
 * @returns {object}                  { [controlColumnName]: watchedValue }
 */
export const getConditionalWatches = (moduleCodeKey, isCreate, watch, resolveDefault) => {
  const watched = {}
  controlFieldsFor(moduleCodeKey, isCreate).forEach((name) => {
    watched[name] = watch(name, resolveDefault(name))
  })
  return watched
}

/**
 * Pure render-side rule: is this module field hidden by a special-field rule? Returns only { hidden }
 * — the submit-side mutation + required-field enforcement stays in validateSpecialForm. No
 * module-level memo, so it is safe across record/module remounts.
 * @param {object} moduleField          the module field ({ module_code_key, column_name })
 * @param {object} watchedValues        from getConditionalWatches
 * @param {object|Array} dropdownOptions  the by-id dropdown-option map (for code_key lookups)
 * @param {boolean} isCreate
 * @returns {{ hidden: boolean }}
 */
export const getFieldRenderRules = (moduleField, watchedValues, dropdownOptions, isCreate) => {
  const rule = SPECIAL_FIELD_RULES[moduleField.module_code_key]?.[moduleField.column_name]
  if (!rule) return { hidden: false }
  if (rule.createOnly && !isCreate) return { hidden: false }
  return { hidden: !!rule.hidden(watchedValues[rule.watch], dropdownOptions) }
}
